// src/modules/app-views/account/pages/Subscription/tabs/TeamTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { Badge, Button, Dropdown, Flex, Tag, Tooltip } from 'antd';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import translateX from 'utils/translateX';
import {
  DeleteOutlined,
  EditOutlined,
  FrownOutlined,
  KeyOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  RestOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo } from 'react';
import RoundedAvatar from 'layouts/SharedComponents/RoundedAvatar';
import dayjs from 'dayjs';
import { BsPauseCircle, BsPlayCircle } from 'react-icons/bs';
import { PiHandCoins } from 'react-icons/pi';
import accountService from 'modules/app-views/account/service';
import appService from 'services/appService';
import TeamInvitationModal from '../../modals/TeamInvitationModal';
import { openModal } from 'components/core/GenericModal';
import DepositSparksModal from '../../modals/DepositSparksModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SubscriptionTeamTab = ({ tabName }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');
  const syncSubscriptionTeamStore = useZustand('auth', 'syncSubscriptionTeam', false);
  const { tab: selectedTab, data, refresh } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();
  // Find current user's profile within the subscription being viewed (not the active one)
  const myMember = useMemo(
    () => (data?.team || []).find(t => t?.accountId === storedUser?._id),
    [data?.team, storedUser?._id]
  );
  const myProfile = myMember?.profile;
  // 'keyUser' = legacy name, 'admin' = new schema name — both allowed
  const canDeposit = ['owner', 'admin', 'keyUser'].includes(myProfile);

  const renderActionsButtons = record => {
    const isSelf = record?.accountId === storedUser?._id;

    // Disable all actions for the logged-in user
    if (isSelf) {
      return (
        <Tooltip title={translateX('you')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled />
        </Tooltip>
      );
    }

    const menuItems = [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: translateX('edit'),
        onClick: () => handleEditUser(record),
      },
    ];

    if (record?.statusLabel === 'invited' || record?.statusLabel === 'expired_invitation') {
      menuItems.push({
        key: 'resend',
        icon: <SendOutlined />,
        label: translateX('resend_invite_email'),
        onClick: () => handleResendInvite(record),
      });
    } else if (record?.statusLabel === 'active') {
      menuItems.push({
        key: 'suspend',
        icon: <BsPauseCircle />,
        label: translateX('suspend_access'),
        onClick: () => handleSuspendAccess(record),
      });
    } else if (record?.statusLabel === 'suspended') {
      menuItems.push({
        key: 'reactivate',
        icon: <BsPlayCircle />,
        label: translateX('reactivate_access'),
        onClick: () => handleReactivateAccess(record),
      });
    }

    if (canDeposit) {
      menuItems.push({
        key: 'deposit',
        icon: <PiHandCoins />,
        label: translateX('deposit_spark_coins'),
        onClick: () => handleDepositSparks(record),
      });
    }

    menuItems.push({
      key: 'remove',
      icon: <RestOutlined />,
      label: translateX('remove'),
      onClick: () => handleRemoveUser(record),
      danger: true,
    });

    const allDisabled = menuItems.every(item => item.disabled);

    return (
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allDisabled} />
        </Tooltip>
      </Dropdown>
    );
  };

  const teamData = useMemo(() => data?.team || [], [data?.team]);

  const columns = buildColumns(renderActionsButtons, canDeposit);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync Zustand stores with the viewed subscription's team whenever it changes.
  // This keeps storedSubscriptions/storedSubscription/storedTeam in sync after
  // refresh() without triggering a full signInRefresh() (which rebuilds routes).
  useEffect(() => {
    if (!data?._id) return;
    syncSubscriptionTeamStore?.(data._id, data?.team || []);
  }, [data?._id, data?.team, syncSubscriptionTeamStore]);



  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDepositSparks = useCallback(record => {
    openModal(DepositSparksModal, { record });
  }, []);

  const handleRemoveUser = useCallback(async record => {
    const userId = record?.invitationId || record?.accountId;
    const content = translateX('remove_user_description', { user: record?.name });

    appService.modal({
      flag: 'confirm',
      title: 'remove_user',
      content,
      okText: 'remove',
      cancelText: 'cancel',
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      onOk: async () => {
        await accountService.removeUser(userId, data?._id);
        await refresh?.({ _id: data?._id });
      },
    });
  }, [refresh, data?._id]);

  const handleResendInvite = useCallback(async record => {
    const name = record?.name;
    const email = record?.email?.value || record?.email;

    if (!email) {
      await appService.message({
        flag: 'e',
        text: 'missing_email_for_invite',
        key: 'resendInvite',
        duration: 4,
      });
      return;
    }

    const content = translateX('resend_invite_description', { name: name, email: email });

    appService.modal({
      flag: 'confirm',
      title: 'resend_invite',
      content,
      okText: 'resend',
      cancelText: 'cancel',
      onOk: async () => {
        await accountService.resendInvitationEmail({ email }, data?._id);
        await refresh?.({ _id: data?._id });
      },
    });
  }, [refresh, data?._id]);

  const handleSuspendAccess = useCallback(record => {
    console.log('handleSuspendAccess', record);
  }, []);

  const handleReactivateAccess = useCallback(record => {
    console.log('handleReactivateAccess', record);
  }, []);

  const handleEditUser = useCallback(
    record => {
      openModal(TeamInvitationModal, {
        editRecord: record,
        team: data?.team || [],
        subscriptionId: data?._id,
        afterClose: () => refresh?.({ _id: data?._id }),
      });
    },
    [refresh, data?._id, data?.team]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      <Flex
        justify="flex-end"
        className="mb-2"
        style={{ width: '100%' }}
        wrap="wrap"
        align="center"
        gutter={16}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="mb-2"
          onClick={() =>
            openModal(TeamInvitationModal, {
              team: data?.team || [],
              subscriptionId: data?._id,
              afterClose: () => refresh?.({ _id: data?._id }),
            })
          }
        >
          {translateX('new_user')}
        </Button>
      </Flex>

      <SimpleTable
        scroll={{ x: 'max-content' }}
        rowKey="_id"
        className="with-checkbox"
        size={isMobile ? 'small' : 'large'}
        columns={columns}
        dataSource={teamData}
        locale={{
          emptyText: (
            <span>
              <FrownOutlined style={{ marginRight: 8 }} />
              {translateX('no_data')}
            </span>
          ),
        }}
        pagination={false}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SubscriptionTeamTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildColumns(renderActionsButtons, canDeposit) {
  return [
    {
      title: translateX('name'),
      dataIndex: 'name',
      sorter: (a, b) => (a?.name || '').localeCompare(b?.name || ''),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => <RoundedAvatar record={record} showName showEmail hideLoading />,
    },
    {
      title: translateX('profile'),
      dataIndex: 'profile',
      filters: [
        {
          text: (
            <Tooltip title={translateX('owner_access')} placement="right">
              <span>{translateX('partner_owner')}</span>
            </Tooltip>
          ),
          value: 'owner',
        },
        {
          text: (
            <Tooltip title={translateX('total_access')} placement="right">
              <span>{translateX('key_user')}</span>
            </Tooltip>
          ),
          value: 'keyUser',
        },
        {
          text: (
            <Tooltip title={translateX('limited_access')} placement="right">
              <span>{translateX('common_user')}</span>
            </Tooltip>
          ),
          value: 'guest',
        },
      ],
      onFilter: (value, record) => record?.profile === value,
      sorter: (a, b) => (a?.profile || '').localeCompare(b?.profile || ''),
      render: (_, record) => (
        <>
          {record?.profile === 'owner' && (
            <Tooltip title={translateX('partner_owner')} placement="top">
              <Flex justify="flex-start" align="center">
                <SafetyCertificateOutlined className="font-size-lg mr-2 font-weight-bold" />
                {translateX('partner_owner')}
              </Flex>
            </Tooltip>
          )}
          {record?.profile === 'keyUser' && (
            <Tooltip title={translateX('key_user_title')} placement="top">
              <Flex justify="flex-start" align="center">
                <KeyOutlined className="font-size-lg mr-2 font-weight-bold" />
                {translateX('key_user')}
              </Flex>
            </Tooltip>
          )}
          {record?.profile === 'guest' && (
            <Tooltip title={translateX('common_user_title')} placement="top">
              <Flex justify="flex-start" align="center">
                <LockOutlined className="font-size-lg mr-2 font-weight-bold" />
                {translateX('common_user')}
              </Flex>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: translateX('status'),
      dataIndex: 'statusLabel',
      width: 200,
      filters: [
        { text: translateX('active'), value: 'active' },
        { text: translateX('invited'), value: 'invited' },
        { text: translateX('suspended'), value: 'suspended' },
        { text: translateX('expired_invitation'), value: 'expired_invitation' },
      ],
      onFilter: (value, record) => record?.statusLabel === value,
      sorter: (a, b) => (a?.statusLabel || '').localeCompare(b?.statusLabel || ''),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => (
        <span>
          {record?.statusLabel && (
            <Tooltip placement="top" title={$translateStatusDescription(record)}>
              <Tag className="text-capitalize" style={{ margin: 4 }} color={record?.statusColor}>
                {translateX(record?.statusLabel)}
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },

    ...(canDeposit
      ? [
          {
            title: translateX('spark_coins'),
            dataIndex: 'sparkCoins',
            align: 'center',
            width: 140,
            sorter: (a, b) => (a?.sparkCoins || 0) - (b?.sparkCoins || 0),
            render: (_, record) => (
              <Badge
                count={record?.sparkCoins || 0}
                showZero
                style={{ backgroundColor: record?.sparkCoins > 0 ? '#faad14' : '#d9d9d9' }}
              />
            ),
          },
        ]
      : []),
    {
      title: translateX('actions'),
      align: 'center',
      width: 60,
      fixed: 'right',
      render: (_, record) => renderActionsButtons(record),
    },
  ];
}

function $translateStatusDescription(record) {
  if (!record?.statusDescription || !record?.invitedAt) return '';
  const dateAgo = dayjs(record?.invitedAt).fromNow();
  const expireAt = dayjs(record?.expireAt).format('L LT');
  const joinedAt = dayjs(record?.joinedAt).format('L LT');

  if (record?.statusLabel === 'expired_invitation') {
    return translateX(record?.statusDescription, { date: expireAt });
  } else {
    return translateX(record?.statusDescription, {
      date: joinedAt,
      date_ago: dateAgo,
      date_expiration: expireAt,
    });
  }
}

